import useUser from '@hooks/user/use-user';
import { useMemo, useState } from 'react';
import { useNegotiation } from '@context/negotiation.context';
import { NegotiationEvent } from '@interfaces/models/negotiation/negotiationEvent';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import useProductQuery from '@hooks/product/use-product-query';
import { useChat } from '@context/chat.context';
import { Product } from '@interfaces/models/product';

const MAX_SELLER_OFFERS = 3;
const hasSellerMadeOffer = (negotiation: Negotiation) => {
  const sellerOffersRemaining = negotiation?.sellerRemainingOffers ?? MAX_SELLER_OFFERS;
  return sellerOffersRemaining < MAX_SELLER_OFFERS;
};

export const useNegotiationModalPresenter = () => {
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { negotiation, initialData } = useNegotiation();
  const productContext = useProductQuery();
  const chatContext = useChat();

  const negotiationProduct = useMemo(() => {
    if (negotiation?.product) {
      return negotiation.product;
    }
    if (chatContext?.product) {
      return chatContext.product;
    }
    if (productContext?.product) {
      return productContext.product;
    }
    return {} as Product;
  }, [productContext?.product, chatContext?.product, negotiation?.product]);

  const role =
    negotiation?.seller.id === user.id ? NegotiationEvent.CreatedByEnum.Seller : NegotiationEvent.CreatedByEnum.Buyer;
  const isBuyer = role === NegotiationEvent.CreatedByEnum.Buyer;
  const concurrentActiveNegotiations = Number(
    negotiationProduct.buyersWhoHaveActiveNegotiation?.length ?? initialData?.product.numberOfOngoingNegotiations ?? 0,
  );
  const isNegotiationBannerVisible = isBuyer && concurrentActiveNegotiations > 0;
  const isBuyNowCTAVisible = isNegotiationBannerVisible && !hasSellerMadeOffer(negotiation) && isBuyer;

  return {
    negotiation,
    initialData,
    negotiationProduct,
    isLoading,
    setIsLoading,
    isBuyNowCTAVisible,
    isNegotiationBannerVisible,
    concurrentActiveNegotiations,
  };
};
